import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types"; // Import PropTypes for validation
import { Button, Modal, Spinner } from "react-bootstrap"; // Import Spinner for loader
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import Loader from "../../../components/Loader";

function OtpVerification({ onClose, email, newEmail }) {
    const { t } = useTranslation();
    const [OTPModal, setOTPModal] = useState(true);
    const [OTPField, setOTPField] = useState(new Array(4).fill(""));
    const [counter, setCounter] = useState(300);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const inputRefs = useRef([]);
    const [emailSend, setEmailSend] = useState(null);
    const user = useSelector((state) => state.auth.user);
    const language = localStorage.getItem("i18nextLng");
    useEffect(() => {
        if (OTPModal) {
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
        }
    }, [OTPModal]);

    useEffect(() => {
        let intervalId;
        if (counter > 0) {
            intervalId = setInterval(() => {
                setCounter((prevCounter) => prevCounter - 1);
            }, 1000);
        }
        return () => clearInterval(intervalId);
    }, [counter]);

    const handleChangeOTP = async (index, e) => {
        const value = e.target.value;
        if (/^\d$/.test(value) || value === "") {
            const newOTPField = [...OTPField];
            newOTPField[index] = value;
            setOTPField(newOTPField);

            if (value && index < OTPField.length - 1) {
                inputRefs.current[index + 1].focus();
            }

            // Check if all inputs are filled
            if (newOTPField.every((digit) => digit !== "")) {
                const otp = newOTPField.join("");
                await verifyOTP(otp);
            }
        }
    };

    const handleKeyDownOTP = (index, e) => {
        if (
            e.key === "Backspace" &&
            !OTPField[index] &&
            index > 0 &&
            inputRefs.current[index - 1]
        ) {
            inputRefs.current[index - 1].focus();
        }
    };

    useEffect(() => {
        const fetchPersonalInfo = async () => {
            try {
                setLoading(true);
                const res = await axios.get(`/employee/get/${user?.userId}`);
                setEmailSend(res.data.response.email);
                handleResendOTP(res.data.response.email);
            } catch (err) {
                toast.error(t("Failed to fetch email. Please try again."));
            } finally {
                setLoading(false);
            }
        };
        if (user?.userId) {
            fetchPersonalInfo();
        }
    }, [user?.userId]);

    const verifyOTP = async (otp) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.put(
                `signup/verify-email-otp?otp=${otp}&email=${email ?? ""}&lang=${language}`
            );

            if (response.data.statusCode === 200) {
                // Update localStorage
                const admin = JSON.parse(localStorage.getItem("user"));
                if (admin?.userData) {
                    admin.userData.isVerified = true;
                    localStorage.setItem("user", JSON.stringify(admin));
                    // Emit a custom event to notify other components
                    const event = new Event("userUpdated");
                    window.dispatchEvent(event);
                }

                toast.success(t("OTP verified successfully!"));
                setOTPModal(false);
                onClose();
            } else {
                setError(response.data.message || t("Failed to verify OTP. Please try again."));
                toast.error(response.data.message || t("Failed to verify OTP. Please try again."));
            }
        } catch (err) {
            console.error("Error verifying OTP:", err);
            setError(t("Failed to verify OTP. Please try again."));
            toast.error(t("Failed to verify OTP. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async (emailSend) => {
        const emailToSend = email || emailSend; 
        if (!emailToSend) {
            toast.error(t("Email not available. Please try again."));
            return;
        }
        try {
            setLoading(true);
            await axios.post("settings/generate-otp", {
                type: 4,
                email: newEmail ? newEmail :emailToSend,
                lang: language,
            });
            toast.success(t("OTP sent successfully!"));
        } catch (err) {
            toast.error(t("Failed to send OTP. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal show={OTPModal} onHide={() => setOTPModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="otp-title">{t("Please Enter your OTP Here")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h6>
                        {t(
                            "Please check your email inbox for the one-time code. Once you've received it, enter it in the OTP box to proceed."
                        )}
                    </h6>
                    <p>Email: {newEmail ? newEmail : emailSend}</p> {/* Display the email */}
                    {loading && (
                        <div className="text-center">
                            <Loader />
                        </div>
                    )}
                    {!loading && (
                        <div className="text-center otp-popup" style={{ marginTop: "14px" }}>
                            {OTPField.map((item, index) => (
                                <input
                                    key={index}
                                    type="text"
                                    value={item}
                                    ref={(input) => (inputRefs.current[index] = input)}
                                    onChange={(e) => handleChangeOTP(index, e)}
                                    onKeyDown={(e) => handleKeyDownOTP(index, e)}
                                    style={{
                                        width: "50px",
                                        margin: "5px",
                                        height: "50px",
                                        textAlign: "center",
                                        fontWeight: "bold",
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            setOTPModal(false);
                            setOTPField(new Array(4).fill(""));
                            setCounter(300);
                            onClose();
                        }}
                    >
                        {t("Close")}
                    </Button>
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            if (counter === 0) {
                                setCounter(300);
                                handleResendOTP(emailSend);
                            }
                        }}
                        disabled={counter > 0 || loading}
                    >
                        {counter > 0
                            ? t("Resend in") + " " + `${counter}` + " " + t("seconds")
                            : t("Resend")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

// Add PropTypes to validate props
OtpVerification.propTypes = {
    onClose: PropTypes.func.isRequired,
    email: PropTypes.string.isRequired,
};

export default OtpVerification;
